import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings, LogOut, User, ReceiptText, Menu } from 'lucide-react';
import lmsApi from '../services/api';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";

interface LMSTopBarProps {
    title: string;
    userName?: string;
    showBack?: boolean;
    onMenuClick?: () => void;
    isSidebarCollapsed?: boolean;
}

const LMSTopBar: React.FC<LMSTopBarProps> = ({
    title,
    userName = "Welcome Back",
    showBack = false,
    onMenuClick,
    isSidebarCollapsed
}) => {
    const navigate = useNavigate();

    const handleLogout = async () => {
        try {
            // Call backend logout to destroy session
            await lmsApi.post('/auth/logout/');
        } catch (error) {
            console.error("Logout API failed", error);
        } finally {
            // Clear local storage/state
            localStorage.removeItem('lms_user_id');
            // Redirect to login
            navigate('/lms/login');
        }
    };

    return (
        <header className="bg-white shadow-sm p-4 sticky top-0 z-10 border-b">
            <div className="flex justify-between items-center px-4 w-full">
                <div className="flex items-center gap-4">
                    {onMenuClick && (
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={onMenuClick}
                            className="mr-2"
                        >
                            <Menu className="h-5 w-5 text-gray-600" />
                        </Button>
                    )}
                    {showBack && !onMenuClick && (
                        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="p-0 hover:bg-transparent">
                            <span className="text-gray-500 hover:text-gray-800">← Back</span>
                        </Button>
                    )}
                    <h1 className="text-xl font-bold text-gray-800 truncate">{title}</h1>
                </div>

                <div className="flex items-center gap-4">
                    <span className="text-sm font-medium text-gray-600 hidden sm:inline-block">
                        {userName}
                    </span>

                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="rounded-full">
                                <Settings className="h-5 w-5 text-gray-600" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56">
                            <DropdownMenuLabel>My Account</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => navigate('/lms/profile')}>
                                <User className="mr-2 h-4 w-4" />
                                <span>Profile</span>
                            </DropdownMenuItem>
                            {window.location.pathname.includes('/staff/') && (
                                <DropdownMenuItem onClick={() => navigate('/lms/staff/transactions')}>
                                    <ReceiptText className="mr-2 h-4 w-4" />
                                    <span>Manage Transactions</span>
                                </DropdownMenuItem>
                            )}
                            <DropdownMenuItem className="text-red-600" onClick={handleLogout}>
                                <LogOut className="mr-2 h-4 w-4" />
                                <span>Log out</span>
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>
        </header>
    );
};

export default LMSTopBar;
